/**
 * 页面配置解析器
 */

export interface ComponentConfig {
  /** 组件 ID */
  id?: string;
  /** 组件类型 */
  type: string;
  /** 组件属性 */
  props?: Record<string, any>;
  /** 子组件 */
  children?: ComponentConfig[];
  /** 样式 */
  style?: React.CSSProperties;
}

export interface PageConfig {
  /** 页面 ID */
  id?: string;
  /** 页面标题 */
  title?: string;
  /** 页面样式 */
  style?: React.CSSProperties;
  /** 组件列表 */
  components: ComponentConfig[];
  /** 数据集配置 */
  dataSets?: Record<string, any>;
  /** 动作配置 */
  actions?: Record<string, string>;
}

/**
 * 解析页面配置 JSON
 */
export const parsePageConfig = (configJson: string): PageConfig => {
  try {
    const config = JSON.parse(configJson);
    return normalizePageConfig(config);
  } catch (error) {
    throw new Error(`Failed to parse page config: ${error}`);
  }
};

/**
 * 标准化页面配置
 */
export const normalizePageConfig = (config: any): PageConfig => {
  if (!config) {
    throw new Error("Page config is empty");
  }

  // 处理 GrapesJS 格式
  if (config.html || config.components) {
    return {
      id: config.id,
      title: config.title,
      style: config.style,
      components: normalizeComponents(config.components || []),
      dataSets: config.dataSets || {},
      actions: config.actions || {},
    };
  }

  return config as PageConfig;
};

/**
 * 标准化组件配置
 */
const normalizeComponents = (components: any[]): ComponentConfig[] => {
  if (!Array.isArray(components)) {
    return [];
  }

  return components.map((comp) => {
    // GrapesJS 组件格式转换
    if (comp.tagName || comp["data-gjs-type"]) {
      return {
        id: comp.attributes?.id || comp.id,
        type: comp["data-gjs-type"] || comp.tagName || "div",
        props: { ...comp.attributes },
        children: comp.components
          ? normalizeComponents(comp.components)
          : undefined,
        style: comp.style,
      };
    }

    return {
      id: comp.id,
      type: comp.type,
      props: comp.props || {},
      children: comp.children ? normalizeComponents(comp.children) : undefined,
      style: comp.style,
    };
  });
};

export default parsePageConfig;
